import { useState, useRef, useEffect } from 'react'
import { collection, addDoc } from 'firebase/firestore'
import { db } from '../config/firebase'
import { useAuth } from '../context/AuthContext'
import { t, tEn, getAppLanguage } from '../i18n/translations'

const RATINGS = [1, 2, 3, 4, 5]

const CATEGORIES = [
  { key: 'general', icon: '\uD83D\uDCAC' },
  { key: 'bug', icon: '\uD83D\uDC1B' },
  { key: 'feature', icon: '\uD83D\uDCA1' },
  { key: 'content', icon: '\uD83D\uDCDD' },
]

const MAX_COMMENT_LENGTH = 1000

export default function FeedbackButton() {
  const { user } = useAuth()
  const [isOpen, setIsOpen] = useState(false)
  const [category, setCategory] = useState('general')
  const [rating, setRating] = useState(0)
  const [comment, setComment] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const panelRef = useRef(null)

  // Close panel when clicking outside
  useEffect(() => {
    if (!isOpen) return
    function handleClick(e) {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setIsOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [isOpen])

  async function handleSubmit(e) {
    e.preventDefault()
    if (!rating && !comment.trim()) return

    setSubmitting(true)
    try {
      await addDoc(collection(db, 'feedback'), {
        category,
        rating,
        comment: comment.trim().slice(0, MAX_COMMENT_LENGTH),
        resolved: false,
        userEmail: user?.email || 'anonymous',
        userName: user?.displayName || 'anonymous',
        appLanguage: getAppLanguage(),
        page: window.location.pathname,
        userAgent: navigator.userAgent,
        createdAt: new Date().toISOString()
      })
      setSubmitted(true)
      setTimeout(() => {
        setIsOpen(false)
        setSubmitted(false)
        setCategory('general')
        setRating(0)
        setComment('')
      }, 2000)
    } catch (err) {
      console.error('Failed to submit feedback:', err)
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="feedback-wrapper" ref={panelRef}>
      <button
        className="feedback-header-btn"
        onClick={() => setIsOpen(!isOpen)}
        title={tEn('feedbackTitle') || t('feedbackTitle')}
      >
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
        </svg>
      </button>

      {isOpen && (
        <div className="feedback-dropdown">
          {submitted ? (
            <div className="feedback-thanks">
              {t('feedbackThanks')}
            </div>
          ) : (
            <form onSubmit={handleSubmit}>
              <h4 className="feedback-title" title={tEn('feedbackTitle')}>{t('feedbackTitle')}</h4>

              <div className="feedback-categories">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat.key}
                    type="button"
                    className={`feedback-cat-btn ${category === cat.key ? 'active' : ''}`}
                    onClick={() => setCategory(cat.key)}
                    title={tEn(`feedbackCat_${cat.key}`) || t(`feedbackCat_${cat.key}`)}
                  >
                    <span className="feedback-cat-icon">{cat.icon}</span>
                    <span className="feedback-cat-label">{t(`feedbackCat_${cat.key}`)}</span>
                  </button>
                ))}
              </div>

              <div className="feedback-rating">
                <span className="feedback-label" title={tEn('feedbackRating')}>{t('feedbackRating')}</span>
                <div className="feedback-stars">
                  {RATINGS.map(n => (
                    <button
                      key={n}
                      type="button"
                      className={`star-btn ${n <= rating ? 'active' : ''}`}
                      onClick={() => setRating(n)}
                    >
                      {n <= rating ? '\u2605' : '\u2606'}
                    </button>
                  ))}
                </div>
              </div>

              <textarea
                className="feedback-textarea"
                placeholder={t('feedbackPlaceholder')}
                value={comment}
                onChange={e => setComment(e.target.value.slice(0, MAX_COMMENT_LENGTH))}
                rows={3}
              />
              {comment.length > 0 && (
                <div className="feedback-char-count">{comment.length}/{MAX_COMMENT_LENGTH}</div>
              )}

              <button
                type="submit"
                className="feedback-submit"
                disabled={submitting || (!rating && !comment.trim())}
                title={tEn('feedbackSend')}
              >
                {submitting ? t('feedbackSending') : t('feedbackSend')}
              </button>
            </form>
          )}
        </div>
      )}
    </div>
  )
}
